import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Card, ErrorNote, Field, Input, Select } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { canEdit, rk, useMe } from '@/lib/hooks';
import type { AgentDetail } from '@/lib/types';

export const AgentVersions = ({ detail }: { detail: AgentDetail }) => {
  const me = useMe();
  const editable = canEdit(me.data) && !detail.agent.archivedAt;
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: rk('agent', detail.agent.id) });
    void qc.invalidateQueries({ queryKey: rk('agents') });
  };
  const live = detail.versions.find((v) => v.id === detail.agent.publishedVersionId);

  const publish = useMutation({
    mutationFn: (versionId: string) => api(`/v1/agents/${detail.agent.id}/versions/${versionId}/publish`, { body: {} }),
    onSuccess: (_d, versionId) => {
      toast.success(`v${detail.versions.find((v) => v.id === versionId)?.version} is live`);
      refresh();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const candidates = detail.versions.filter((v) => v.id !== detail.agent.publishedVersionId);
  const [canary, setCanary] = useState({ versionId: detail.agent.canaryVersionId ?? candidates[0]?.id ?? '', percent: detail.agent.canaryPercent || 10 });
  const setCanaryM = useMutation({
    mutationFn: (body: { versionId: string | null; percent: number }) => api(`/v1/agents/${detail.agent.id}/canary`, { body }),
    onSuccess: (_d, body) => {
      toast.success(body.percent ? `Canary: ${body.percent}% of traffic` : 'Canary stopped');
      refresh();
    },
  });

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
      <Card title="History">
        <ul>
          {detail.versions.map((v) => {
            const isLive = v.id === detail.agent.publishedVersionId;
            const isCanary = v.id === detail.agent.canaryVersionId && detail.agent.canaryPercent > 0;
            return (
              <li key={v.id} className="flex flex-wrap items-center gap-3 border-b border-line-2 px-4 py-3 last:border-0">
                <span className="w-10 font-semibold tabular-nums">v{v.version}</span>
                <div className="flex gap-1">
                  {isLive && <Badge tone="ok">live</Badge>}
                  {isCanary && <Badge tone="brand">canary {detail.agent.canaryPercent}%</Badge>}
                  {v.status === 'draft' && <Badge tone="warn">draft</Badge>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{v.changelog || <span className="text-ink-3">No note</span>}</div>
                  <div className="text-xs text-ink-3">
                    {v.publishedAt ? `Published ${fmtDateTime(v.publishedAt)} by ${v.publishedBy ?? '—'}` : `Edited ${fmtDateTime(v.updatedAt)} by ${v.createdBy ?? '—'}`}
                    {' · '}
                    {'model' in v.config ? v.config.model.name : `flow · ${v.config.nodes.length} steps`}
                  </div>
                </div>
                {editable && !isLive && v.status !== 'draft' && (
                  <Button
                    size="sm"
                    loading={publish.isPending && publish.variables === v.id}
                    onClick={() => confirm(`Make v${v.version} live again${live ? ` (replacing v${live.version})` : ''}?`) && publish.mutate(v.id)}
                  >
                    {live && v.version < live.version ? 'Roll back to this' : 'Make live'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      <Card title="Canary">
        <div className="flex flex-col gap-3 p-4">
          <p className="text-[13px] text-ink-2">
            Send a share of traffic to another version. The same user always gets the same version, so conversations stay consistent. Compare the two in Requests, then publish or stop.
          </p>
          {!live ? (
            <p className="text-xs text-ink-3">Publish a version first.</p>
          ) : candidates.length === 0 ? (
            <p className="text-xs text-ink-3">Save a draft to have something to canary.</p>
          ) : (
            <>
              <Field label="Version">
                <Select disabled={!editable} value={canary.versionId} onChange={(e) => setCanary({ ...canary, versionId: e.target.value })}>
                  {candidates.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version} {v.status === 'draft' ? '(draft — will be frozen)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={`Traffic: ${canary.percent}%`}>
                <Input type="range" min={1} max={50} disabled={!editable} value={canary.percent} onChange={(e) => setCanary({ ...canary, percent: Number(e.target.value) })} className="h-auto border-0 px-0 accent-ink" />
              </Field>
              <div className="flex gap-2">
                <Button variant="primary" disabled={!editable} loading={setCanaryM.isPending} onClick={() => setCanaryM.mutate({ versionId: canary.versionId, percent: canary.percent })}>
                  {detail.agent.canaryPercent ? 'Update canary' : 'Start canary'}
                </Button>
                {detail.agent.canaryPercent > 0 && (
                  <Button variant="danger" disabled={!editable} onClick={() => setCanaryM.mutate({ versionId: null, percent: 0 })}>
                    Stop
                  </Button>
                )}
              </div>
              {setCanaryM.error ? <ErrorNote error={setCanaryM.error} /> : null}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
